import { toast } from "@imagedesignai/neo";
import {
  type ImgHTMLAttributes,
  useEffect,
  useRef,
  useState,
  type VideoHTMLAttributes,
} from "react";
import type { GifmeApiClient } from "./api-client.ts";
import type { MediaCache } from "./media-cache.ts";

export function useCachedMediaUrl(
  cache: MediaCache,
  path: string | null,
  enabled = true,
): string | null {
  const [url, setUrl] = useState<string | null>(() => (path ? cache.peek(path) : null));

  useEffect(() => {
    setUrl(path ? cache.peek(path) : null);
    if (!path || !enabled) return;
    const controller = new AbortController();
    const release = cache.hold(path);
    void cache
      .get(path, controller.signal)
      .then(setUrl)
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          toast.danger("Could not load media");
        }
      });
    return () => {
      controller.abort();
      release();
    };
  }, [cache, enabled, path]);

  return url;
}

export function useNearViewport<T extends Element>(lazy = true) {
  const targetRef = useRef<T>(null);
  const [isNearViewport, setIsNearViewport] = useState(!lazy);

  useEffect(() => {
    const target = targetRef.current;
    if (!lazy || !target) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsNearViewport(entry?.isIntersecting ?? false),
      { rootMargin: "50%" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [lazy]);

  return { targetRef, isNearViewport };
}

interface CachedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "alt"> {
  alt: string;
  cache: MediaCache;
  path: string;
  lazy?: boolean;
}

export function CachedImage({ alt, cache, path, lazy = false, ...props }: CachedImageProps) {
  const { targetRef, isNearViewport } = useNearViewport<HTMLImageElement>(lazy);
  const url = useCachedMediaUrl(cache, path, isNearViewport);

  return <img {...props} ref={targetRef} src={url ?? undefined} alt={alt} />;
}

interface OwnedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "alt"> {
  alt: string;
  api: GifmeApiClient;
  path: string;
}

export function OwnedImage({ alt, api, path, ...props }: OwnedImageProps) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let ownedUrl: string | null = null;
    void api
      .mediaBlob(path, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        ownedUrl = URL.createObjectURL(blob);
        setUrl(ownedUrl);
      })
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          toast.danger("Could not load media");
        }
      });
    return () => {
      controller.abort();
      if (ownedUrl) URL.revokeObjectURL(ownedUrl);
    };
  }, [api, path]);

  return <img {...props} src={url ?? undefined} alt={alt} />;
}

interface OwnedVideoProps extends Omit<VideoHTMLAttributes<HTMLVideoElement>, "aria-label"> {
  api: GifmeApiClient;
  label: string;
  path: string;
}

export function OwnedVideo({ api, label, path, ...props }: OwnedVideoProps) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let ownedUrl: string | null = null;
    void api
      .mediaBlob(path, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        ownedUrl = URL.createObjectURL(blob);
        setUrl(ownedUrl);
      })
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          toast.danger("Could not load media");
        }
      });
    return () => {
      controller.abort();
      if (ownedUrl) URL.revokeObjectURL(ownedUrl);
    };
  }, [api, path]);

  return <video {...props} src={url ?? undefined} aria-label={label} />;
}
